import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus, Search, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { errMsg, fmtPct, fmtPeriod, useApi } from "@/lib/api";
import { NoData } from "@/components/NoData";

const KINDS = [["all", "All types"], ["individual", "Individual"], ["trust", "Trust"], ["entity", "Entity"]];

const EntityPick = ({ value, entities, onChange, testid, placeholder }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className="h-9 rounded-sm bg-white text-sm min-w-0 [&>span]:truncate" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
  </Select>
);

const KindPick = ({ value, onChange, testid }) => (
  <Select value={value || "all"} onValueChange={onChange}>
    <SelectTrigger className="h-9 rounded-sm bg-white text-sm min-w-0 [&>span]:truncate" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{KINDS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const ofKind = (entities, kind, keepId) => (!kind || kind === "all" ? entities : entities.filter((e) => e.kind === kind || e.id === keepId));

function TypedPick({ value, entities, onChange, testid, placeholder }) {
  const cur = entities.find((e) => e.id === value);
  const [kind, setKind] = useState(cur?.kind || "all");
  const pickKind = (k) => { setKind(k); if (cur && k !== "all" && cur.kind !== k) onChange(""); };
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 min-w-0">
      <KindPick value={kind} onChange={pickKind} testid={`${testid}-type`} />
      <EntityPick value={value} entities={ofKind(entities, kind, value)} onChange={onChange} testid={testid} placeholder={placeholder} />
    </div>
  );
}

function Group({ ownedId, name, rows, entities, update, remove, addOwner }) {
  const total = rows.reduce((s, r) => s + (r.pct === "" || r.pct == null ? 0 : Number(r.pct)), 0);
  const missing = rows.some((r) => r.pct === "" || r.pct == null);
  const ok = !missing && Math.abs(total - 100) <= 0.05;
  return (
    <div className="panel p-5 min-w-0" data-testid={`own-group-${ownedId}`}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <h3 className="font-serif text-xl text-slate-900 min-w-0 break-words">{name}</h3>
        <span className={`${ok ? "ok-badge" : "warn-badge"} shrink-0`} data-testid={`own-total-${ownedId}`}>
          {!ok && <AlertTriangle className="h-3 w-3" />}{fmtPct(total)}{missing ? " · missing %" : ""}
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_6.5rem_auto] gap-2 items-center">
            <div className="col-span-2 sm:col-span-1 min-w-0"><TypedPick value={r.owner_id} entities={entities} onChange={(v) => update(r.key, { owner_id: v })} testid={`own-owner-${r.key}`} placeholder="Owner" /></div>
            <div className="relative">
              <Input type="number" step="0.0001" min="0" max="100" value={r.pct ?? ""}
                onChange={(e) => update(r.key, { pct: e.target.value })}
                className={`h-9 rounded-sm font-mono text-right pr-7 ${r.pct === "" || r.pct == null ? "border-red-300 bg-red-50" : ""}`}
                data-testid={`ownership-percentage-input-${r.key}`} />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(r.key)} data-testid={`own-remove-${r.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <button onClick={() => addOwner(ownedId)} className="mt-3 text-xs text-slate-500 hover:text-slate-900 transition-colors duration-200 flex items-center gap-1" data-testid={`own-add-owner-${ownedId}`}>
        <Plus className="h-3 w-3" /> Add owner
      </button>
    </div>
  );
}

let seq = 0;
const withKey = (r) => ({ ...r, key: `r${++seq}` });

export default function Ownership() {
  const api = useApi();
  const { period, me } = useApp();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [newOwned, setNewOwned] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!period) return;
    api.get("/ownership", { params: { period } }).then((r) => {
      setData(r.data);
      setRows(r.data.items.map(withKey));
    });
  }, [api, period]);

  useEffect(() => {
    load();
  }, [load]);

  const names = useMemo(() => Object.fromEntries((data?.entities || []).map((e) => [e.id, e.name])), [data]);
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.owned_id] = g[r.owned_id] || []).push(r); });
    return Object.entries(g)
      .map(([id, rs]) => ({ id, name: names[id] || "?", rows: rs }))
      .filter((x) => `${x.name} ${x.rows.map((r) => names[r.owner_id]).join(" ")}`.toLowerCase().includes(q.toLowerCase()))
      .filter((x) => {
        if (!onlyIssues) return true;
        const t = x.rows.reduce((s, r) => s + Number(r.pct || 0), 0);
        return x.rows.some((r) => r.pct === "" || r.pct == null) || Math.abs(t - 100) > 0.05;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, names, q, onlyIssues]);

  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key) => setRows((rs) => rs.filter((r) => r.key !== key));
  const addOwner = (owned_id) => setRows((rs) => [...rs, withKey({ owned_id, owner_id: "", pct: "" })]);

  const save = async () => {
    const items = rows.filter((r) => r.owner_id && r.owned_id && r.pct !== "" && r.pct != null)
      .map((r) => ({ owner_id: r.owner_id, owned_id: r.owned_id, pct: Number(r.pct) }));
    setSaving(true);
    try {
      await api.put("/ownership", { items }, { params: { period } });
      toast.success(`Saved ${items.length} ownership rows for ${fmtPeriod(period)}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!period) return <NoData />;
  if (!data) return <p className="small-label">Loading…</p>;
  return (
    <div className="max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Ownership</h1>
          <p className="text-slate-500 mt-3 max-w-2xl" data-testid="own-effective-note">
            Editing percentages for month end <b className="text-slate-800">{fmtPeriod(period)}</b>.{" "}
            {data.effective_period
              ? data.effective_period === period ? "These were saved for this month end." : `Currently carried forward from ${fmtPeriod(data.effective_period)}${data.updated_by ? ` (${data.updated_by})` : ""}. Saving creates a set for this month end; later months inherit it.`
              : "No percentages saved yet."}
          </p>
        </div>
        <Button onClick={save} disabled={saving || !me?.access?.can_edit} className="rounded-sm h-11 px-6" data-testid="own-save-button"><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save percentages"}</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-10">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entity or owner" className="pl-9 w-full rounded-sm bg-white" data-testid="own-search-input" />
        </div>
        <button onClick={() => setOnlyIssues((v) => !v)} data-testid="own-issues-toggle"
          className={`px-4 py-2 text-sm border transition-colors duration-200 ${onlyIssues ? "bg-red-50 border-red-200 text-red-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          Needs attention only
        </button>
        <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <div className="flex-1 sm:w-96 min-w-0"><TypedPick value={newOwned} entities={data.entities} onChange={setNewOwned} testid="own-new-owned-select" placeholder="Add owners for entity…" /></div>
          <Button variant="outline" disabled={!newOwned} onClick={() => { addOwner(newOwned); setNewOwned(""); }} data-testid="own-add-group-button"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Rows in red come from "calculated" lines in the Investments upload and still need a percentage.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
        {groups.map((g) => (
          <Group key={g.id} ownedId={g.id} name={g.name} rows={g.rows} entities={data.entities} update={update} remove={remove} addOwner={addOwner} />
        ))}
      </div>
    </div>
  );
}
